// PDF engine for the website print flow.
//
// The mobile app rearranges every file on the device before uploading it (Android
// MainActivity.rearrangePdfPages / rearrangePdfPagesForPost). The partner shop's printer
// agent expects exactly that layout (page groups in the order: single B&W, single colour,
// two-sided B&W, two-sided colour, two-sided mixed; N-up already applied), so the website
// does the same work in the browser with pdf-lib.
import { pdfjs } from "react-pdf";
import { parseNumberString } from "./pageRanges";

pdfjs.GlobalWorkerOptions.workerSrc = `/pdf.worker.${pdfjs.version}.min.mjs`; // same pdf.js version as react-pdf, copied by scripts/copy-pdf-worker.js

let pdfLibPromise = null;
export function loadPdfLib() {
  if (!pdfLibPromise) pdfLibPromise = import("pdf-lib");
  return pdfLibPromise;
}

// Same point sizes as the app (iText PageSize constants).
export const PAGE_SIZES = {
  A4: [595, 842],
  A3: [842, 1191],
  A2: [1191, 1684],
  A1: [1684, 2384],
  A0: [2384, 3370],
  Legal: [612, 1008],
};
export const PAGE_SIZE_OPTIONS = ["A4", "A3", "A2", "A1", "A0", "Legal"];
export const PAGES_PER_SHEET_OPTIONS = [1, 2, 4, 6, 9, 16];
export const MAX_FILE_BYTES = 50 * 1024 * 1024;
export const MAX_FILES = 15;

export function pageDimensions(option, landscape) {
  const [w, h] = PAGE_SIZES[option] || PAGE_SIZES.A4;
  return landscape ? [h, w] : [w, h];
}

export class PdfError extends Error {}

async function loadSource(bytes) {
  const { PDFDocument } = await loadPdfLib();
  try {
    const doc = await PDFDocument.load(bytes, { ignoreEncryption: true, updateMetadata: false });
    if (doc.isEncrypted) {
      // pdf-lib can read the structure but copying pages from an encrypted file is not
      // reliable; the app asks the user to remove the password first, so do the same.
      try {
        await PDFDocument.create().then((d) => d.copyPages(doc, [0]));
      } catch (e) {
        throw new PdfError("This PDF is password protected. Please remove the password and upload it again.");
      }
    }
    return doc;
  } catch (e) {
    if (e instanceof PdfError) throw e;
    throw new PdfError("This file could not be read as a PDF. Please try another file.");
  }
}

/** Reads the page count (and verifies the PDF can be processed). */
export async function inspectPdf(bytes) {
  const doc = await loadSource(bytes);
  const pageCount = doc.getPageCount();
  if (!pageCount) throw new PdfError("This PDF has no pages.");
  return { pageCount };
}

/** JPG / PNG -> single-page A4 PDF (portrait or landscape to match the photo). */
export async function imageToPdf(bytes, mime) {
  const { PDFDocument } = await loadPdfLib();
  const doc = await PDFDocument.create();
  let image;
  try {
    image = mime === "image/png" ? await doc.embedPng(bytes) : await doc.embedJpg(bytes);
  } catch (e) {
    throw new PdfError("This image could not be read. Please use a JPG or PNG file.");
  }
  const landscape = image.width > image.height;
  const [pw, ph] = pageDimensions("A4", landscape);
  const margin = 18;
  const scale = Math.min((pw - 2 * margin) / image.width, (ph - 2 * margin) / image.height);
  const w = image.width * scale;
  const h = image.height * scale;
  const page = doc.addPage([pw, ph]);
  page.drawImage(image, { x: (pw - w) / 2, y: (ph - h) / 2, width: w, height: h });
  return doc.save({ useObjectStreams: true });
}

function addInGroups(groups, pages, pagesPerSheet) {
  if (!pages || !pages.length) return;
  for (let i = 0; i < pages.length; i += pagesPerSheet) {
    groups.push(pages.slice(i, i + pagesPerSheet));
  }
}

// Port of MainActivity.renderGroups (same margins, slot maths and scaling).
/**
 * Greys out an area the same way the app does (iText PdfGState with the Saturation blend
 * mode): a black rectangle blended with "Saturation" removes all colour underneath it.
 * pdf-lib only exposes the separable blend modes, so the ExtGState is written by hand.
 */
function drawSaturationRect(page, lib, { x, y, width, height }) {
  const { PDFName, pushGraphicsState, popGraphicsState, setGraphicsState, rgb } = lib;
  try {
    const key = "GSsat";
    const existing = page.node.Resources && page.node.Resources().lookup(PDFName.of("ExtGState"));
    if (!existing || !existing.has || !existing.has(PDFName.of(key))) {
      page.node.setExtGState(PDFName.of(key), page.doc.context.obj({ Type: "ExtGState", BM: "Saturation" }));
    }
    page.pushOperators(pushGraphicsState(), setGraphicsState(key));
    page.drawRectangle({ x, y, width, height, color: rgb(0, 0, 0) });
    page.pushOperators(popGraphicsState());
  } catch (e) {
    /* greyscale is cosmetic for the preview; B&W printers print mono anyway */
  }
}

async function renderGroups({ srcDoc, outDoc, groups, pagesPerSheet, pageSize, scalePercentage, grayPages, rgb, lib }) {
  const [pageWidth, pageHeight] = pageSize;
  let rows = Math.floor(Math.sqrt(pagesPerSheet));
  if (rows < 1) rows = 1;
  const cols = Math.floor(pagesPerSheet / rows);
  const edgeMargin = 5;
  const spacingMargin = pagesPerSheet > 1 ? 10 : 0;
  const usableWidth = pageWidth - 2 * edgeMargin;
  const usableHeight = pageHeight - 2 * edgeMargin;
  const slotWidth = usableWidth / cols - spacingMargin;
  const slotHeight = usableHeight / rows - spacingMargin;

  const srcCount = srcDoc.getPageCount();
  const needed = Array.from(new Set(groups.flat())).filter((p) => p >= 1 && p <= srcCount);
  const embedded = needed.length ? await outDoc.embedPdf(srcDoc, needed.map((p) => p - 1)) : [];
  const embeddedByPage = new Map(needed.map((p, k) => [p, embedded[k]]));

  for (const group of groups) {
    const page = outDoc.addPage([pageWidth, pageHeight]);
    let currentPage = 0;
    for (const pageIndex of group) {
      const emb = embeddedByPage.get(pageIndex);
      if (!emb) continue;
      const positionOnSheet = currentPage % pagesPerSheet;
      const row = Math.floor(positionOnSheet / cols);
      const col = positionOnSheet % cols;
      const scale = Math.min(slotWidth / emb.width, slotHeight / emb.height) * (scalePercentage / 100);
      const contentWidth = emb.width * scale;
      const contentHeight = emb.height * scale;
      const x = edgeMargin + col * (slotWidth + spacingMargin) + (slotWidth - contentWidth) / 2;
      const y = pageHeight - edgeMargin - (row + 1) * (slotHeight + spacingMargin) + (slotHeight - contentHeight) / 2;
      const gray = grayPages && grayPages.has(pageIndex);
      if (gray) page.drawRectangle({ x, y, width: contentWidth, height: contentHeight, color: rgb(1, 1, 1) });
      page.drawPage(emb, { x, y, xScale: scale, yScale: scale });
      if (gray) drawSaturationRect(page, lib, { x, y, width: contentWidth, height: contentHeight });
      currentPage += 1;
    }
  }
}

/**
 * Port of rearrangePdfPages: five page groups, B&W pages rendered in greyscale.
 * Page numbers are 1-based.
 */
export async function rearrangePdf(srcBytes, options) {
  const lib = await loadPdfLib();
  const { PDFDocument, rgb } = lib;
  const { pagesPerSheet = 1, landscape = false, scale = 100, pageSize = "A4", grayscale = true } = options;
  const singleBW = options.singleBW || [];
  const singleColor = options.singleColor || [];
  const bothBW = options.bothBW || [];
  const bothColor = options.bothColor || [];
  const mixed = options.mixed || [];
  const srcDoc = await loadSource(srcBytes);
  const outDoc = await PDFDocument.create();
  const groups = [];
  addInGroups(groups, singleBW, pagesPerSheet);
  addInGroups(groups, singleColor, pagesPerSheet);
  addInGroups(groups, bothBW, pagesPerSheet);
  addInGroups(groups, bothColor, pagesPerSheet);
  addInGroups(groups, mixed, pagesPerSheet);
  const grayPages = new Set();
  if (grayscale) {
    singleBW.forEach((p) => grayPages.add(p));
    bothBW.forEach((p) => grayPages.add(p));
    mixed.forEach((p, i) => {
      if (i % 2 === 0) grayPages.add(p);
    });
  }
  await renderGroups({ srcDoc, outDoc, groups, pagesPerSheet, pageSize: pageDimensions(pageSize, landscape), scalePercentage: scale, grayPages, rgb, lib });
  return outDoc.save({ useObjectStreams: true });
}

/** Port of rearrangePdfPagesForPost: single-sided pages then two-sided pages, no greyscale. */
export async function rearrangePdfForPost(srcBytes, options) {
  const lib = await loadPdfLib();
  const { PDFDocument, rgb } = lib;
  const { pagesPerSheet = 1, landscape = false, scale = 100, pageSize = "A4" } = options;
  const srcDoc = await loadSource(srcBytes);
  const outDoc = await PDFDocument.create();
  const groups = [];
  addInGroups(groups, options.singlePages || [], pagesPerSheet);
  addInGroups(groups, options.bothSidePages || [], pagesPerSheet);
  await renderGroups({ srcDoc, outDoc, groups, pagesPerSheet, pageSize: pageDimensions(pageSize, landscape), scalePercentage: scale, grayPages: null, rgb, lib });
  return outDoc.save({ useObjectStreams: true });
}

/**
 * The bytes that get uploaded with the order. Mirrors PrinterController.initiatePayment
 * in the app: simple jobs use the "ForPost" layout, everything else the five-group layout.
 * `computed` is the result of pricing.computeFile for this file.
 */
export async function buildUploadPdf(file, computed) {
  const c = file.config;
  const common = { pagesPerSheet: c.pagesPerSheet, landscape: c.landscape, scale: Math.trunc(c.scale), pageSize: c.pageSize };
  const simple =
    c.pagesPerSheet === 1 ||
    (computed.singleBWPages.length && !computed.singleColorPages.length && !computed.bothSideColorPages.length) ||
    (computed.bothSideBWPages.length && !computed.singleColorPages.length && !computed.bothSideColorPages.length);
  if (simple) {
    return rearrangePdfForPost(file.bytes, {
      ...common,
      singlePages: [...computed.singleBWPages, ...computed.singleColorPages],
      bothSidePages: [...computed.bothSideBWPages, ...computed.bothSideColorPages],
    });
  }
  let singleBW = parseNumberString(c.singleBW);
  let singleColor = parseNumberString(c.singleColor);
  const bothBW = parseNumberString(c.bothBW);
  const bothColor = parseNumberString(c.bothColor);
  const mixed = parseNumberString(c.mixed);
  if (!singleBW.length && !singleColor.length && !bothBW.length && !bothColor.length && !mixed.length) {
    const all = parseNumberString(`1-${file.pageCount}`);
    if (c.printType !== "BW") singleColor = all;
    else singleBW = all;
  }
  return rearrangePdf(file.bytes, { ...common, singleBW, singleColor, bothBW, bothColor, mixed, grayscale: true });
}

/** Preview shown while configuring: print order of pages with B&W pages in greyscale. */
export async function buildPreviewPdf(file, computed) {
  const c = file.config;
  return rearrangePdf(file.bytes, {
    pagesPerSheet: c.pagesPerSheet,
    landscape: c.landscape,
    scale: Math.trunc(c.scale),
    pageSize: c.pageSize,
    singleBW: computed.singleBWPages,
    singleColor: computed.singleColorPages,
    bothBW: computed.bothSideBWPages,
    bothColor: computed.bothSideColorPages,
    mixed: computed.bothSideBWAndColorPages,
    grayscale: true,
  });
}

/**
 * Client-side version of /configuration/detect: renders each page small and checks whether
 * every pixel is (near) grey. Returns 1-based page numbers.
 */
export async function detectColorPages(bytes, onProgress) {
  const copy = new Uint8Array(bytes.byteLength);
  copy.set(bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes));
  const doc = await pdfjs.getDocument({ data: copy }).promise;
  const bwRange = [];
  const colorRange = [];
  try {
    for (let i = 1; i <= doc.numPages; i++) {
      const page = await doc.getPage(i);
      const base = page.getViewport({ scale: 1 });
      const viewport = page.getViewport({ scale: Math.min(1, 160 / base.width) });
      const canvas = document.createElement("canvas");
      canvas.width = Math.ceil(viewport.width);
      canvas.height = Math.ceil(viewport.height);
      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      await page.render({ canvasContext: ctx, viewport }).promise;
      const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
      let gray = true;
      for (let p = 0; p < data.length; p += 4) {
        const r = data[p];
        const g = data[p + 1];
        const b = data[p + 2];
        if (Math.abs(r - g) > 10 || Math.abs(g - b) > 10 || Math.abs(r - b) > 10) {
          gray = false;
          break;
        }
      }
      (gray ? bwRange : colorRange).push(i);
      page.cleanup();
      if (onProgress) onProgress(i, doc.numPages);
    }
  } finally {
    await doc.destroy();
  }
  return { bwRange, colorRange };
}

export function toBlobUrl(bytes) {
  return URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
}
